import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { PortalPageHeader } from "@/components/portal-page-header";
import type { OrganizationUserAdminDTO } from "@/orval/model";
import { OrganizationRoleType } from "@/orval/model";
import { useAdminInvitationCreateInvitation } from "@/orval/admin-invitation";
import {
  getTeacherGetTeachersFromOrganizationQueryKey,
  getTeacherGetTeachersFromOrganizationQueryOptions,
  teacherGetTeachersFromOrganization,
} from "@/orval/teacher";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDownIcon, PlusIcon } from "lucide-react";
import { EditOrganizationUserDialog } from "./-components/edit-organization-user-dialog";
import { useMemo, useState } from "react";
import { CreateAnonymousTeacherDialog } from "./-components/create-anonymous-teacher-dialog";
import { InviteAnonymousTeacherButton } from "./-components/invite-anonymous-teacher-button";
import { InviteTeacherDialog } from "./-components/invite-teacher-dialog";
import { toast } from "sonner";
import { AssignExistingParentTeacherDialog } from "./-components/assign-existing-parent-teacher-dialog";
import { AccountStatusBadge } from "./-components/account-status-badge";

type TeacherActionDialog = "anonymous" | "invite" | "assign-parent" | null;

const OrganizationAdminTeachersPage: React.FC = () => {
  const { organizationId } = Route.useParams();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserAdminDTO | null>(null);
  const [teacherActionDialog, setTeacherActionDialog] = useState<TeacherActionDialog>(null);
  const { mutateAsync: createInvitation, isPending: isCreatingInvitation } = useAdminInvitationCreateInvitation();

  const handleRowClick = (teacher: OrganizationUserAdminDTO) => {
    setSelectedUser(teacher);
    setDialogOpen(true);
  };

  const handleInviteAnonymousTeacher = async (teacher: OrganizationUserAdminDTO) => {
    const email = teacher.email?.trim();

    if (!teacher.isAnonymous || !email) {
      return;
    }

    await createInvitation({
      organizationId,
      data: {
        email,
        targetRoleType: OrganizationRoleType.TEACHER,
      },
    });

    toast.success("Teacher invitation sent.");
  };

  const columns = useMemo<ColumnDef<OrganizationUserAdminDTO>[]>(
    () => [
      {
        accessorKey: "firstName",
        header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
        enableSorting: true,
      },
      {
        accessorKey: "lastName",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
        enableSorting: true,
      },
      {
        accessorKey: "email",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
      },
      {
        accessorKey: "phoneNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Phone Number" />,
      },
      {
        accessorKey: "isAnonymous",
        header: "Account",
        cell: ({ row }) => <AccountStatusBadge isAnonymous={row.original.isAnonymous} />,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const teacher = row.original;

          if (!teacher.isAnonymous) {
            return null;
          }

          const email = teacher.email?.trim();
          const disabledReason = email ? undefined : "This button will be enabled when an email address is provided.";

          if (teacher.hasPendingTeacherInvite) {
            return (
              <div className="inline-flex" onClick={(event) => event.stopPropagation()}>
                <Button variant="outline" size="sm" disabled>
                  Pending Teacher Invite
                </Button>
              </div>
            );
          }

          return (
            <div className="inline-flex" onClick={(event) => event.stopPropagation()}>
              <InviteAnonymousTeacherButton
                variant="outline"
                size="sm"
                disabledReason={disabledReason}
                isPending={isCreatingInvitation}
                onClick={() => void handleInviteAnonymousTeacher(teacher)}
              >
                Invite
              </InviteAnonymousTeacherButton>
            </div>
          );
        },
      },
    ],
    [handleInviteAnonymousTeacher, isCreatingInvitation],
  );

  const getTeachers = teacherGetTeachersFromOrganization;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Teachers"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <PlusIcon className="mr-2 size-4" />
                  Add Teacher
                  <ChevronDownIcon className="ml-2 size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setTeacherActionDialog("assign-parent")}>
                  Assign Existing Parent
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setTeacherActionDialog("anonymous")}>
                  Add New Unlinked Profile
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setTeacherActionDialog("invite")}>
                  Invite New Teacher
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <DataTable
        columns={columns}
        queryFn={(params) => getTeachers(organizationId, params)}
        queryKeyFn={(params) => getTeacherGetTeachersFromOrganizationQueryKey(organizationId, params)}
        onRowClick={handleRowClick}
        getRowIdFn={(row) => row.id.toString()}
      />

      {selectedUser && (
        <EditOrganizationUserDialog isOpen={isDialogOpen} onClose={() => setDialogOpen(false)} user={selectedUser} />
      )}

      <CreateAnonymousTeacherDialog
        organizationId={organizationId}
        isOpen={teacherActionDialog === "anonymous"}
        onClose={() => setTeacherActionDialog(null)}
      />

      <AssignExistingParentTeacherDialog
        organizationId={organizationId}
        isOpen={teacherActionDialog === "assign-parent"}
        onClose={() => setTeacherActionDialog(null)}
      />

      <InviteTeacherDialog
        organizationId={organizationId}
        isOpen={teacherActionDialog === "invite"}
        onClose={() => setTeacherActionDialog(null)}
      />
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/teachers")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getTeacherGetTeachersFromOrganizationQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
  component: OrganizationAdminTeachersPage,
});
